// AdminLayout.tsx
import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useAppDispatch, useAppSelector } from '../Modules/Presentation/hooks';
import { selectUser } from '../Modules/User/Presentation/selectors';
import { logout } from '../Modules/User/Presentation/reducers';
import { refreshMeThunk } from '../Modules/User/Presentation/actions';
import { MenuOutlined, CloseOutlined, DownOutlined, RightOutlined, BellOutlined, TeamOutlined, AppstoreOutlined, SearchOutlined, LinkOutlined, FileTextOutlined, UserOutlined, LogoutOutlined, SettingOutlined } from '@ant-design/icons';
import { Drawer } from 'antd';
import { stripSuffix } from '../Utils/usernameSuffix';
import { isLimitedPartnerAdmin } from '../Utils/limitedPartnerAdmin';

const Layout = styled.div`display:flex;height:100vh;`;

const Sidebar = styled.aside<{ collapsed: boolean }>`
  width: ${({ collapsed }) => (collapsed ? '60px' : '220px')};
  background: linear-gradient(180deg, #2a0a0f 0%, #3D0301 100%);
  color:#fff;
  display:flex;
  flex-direction:column;
  transition: width 0.3s ease;
  align-items: ${({ collapsed }) => (collapsed ? 'center' : '')};
`;

const Logo = styled.h1<{ collapsed: boolean }>`
  font-size:1.4rem;
  font-weight:700;
  padding:24px;
  margin:0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${({ collapsed }) => collapsed && css`text-align: center; font-size: 1.2rem; padding: 24px 0;`}
`;

const Menu = styled.nav`
  flex:1;
  display:flex;
  flex-direction:column;
`;

const BottomArea = styled.div`
  margin-top: auto;
  width: 100%;
`;

const Item = styled(NavLink) <{ collapsed: boolean }>`
  padding: 16px 24px;
  color: inherit;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: ${({ collapsed }) => (collapsed ? '0' : '12px')};
  justify-content: ${({ collapsed }) => (collapsed ? 'center' : 'flex-start')};

  &.active {
    background: #B03052;
    font-weight: 600;
  }
  &:hover {
    background: rgba(176, 48, 82, 0.18);
  }
`;

const SubItem = styled(NavLink) <{ collapsed: boolean }>`
  padding: 12px 24px 12px 48px;
  color: inherit;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: ${({ collapsed }) => (collapsed ? '0' : '12px')};
  justify-content: ${({ collapsed }) => (collapsed ? 'center' : 'flex-start')};
  font-size: 0.9em;

  &.active {
    background: #B03052;
    font-weight: 600;
  }
  &:hover {
    background: rgba(176, 48, 82, 0.18);
  }
`;

const SubMenuContainer = styled.div<{ collapsed: boolean; isOpen: boolean }>`
  display: ${({ collapsed, isOpen }) => (collapsed ? 'none' : isOpen ? 'block' : 'none')};
  transition: all 0.3s ease;
`;

const MenuHeader = styled.div<{ collapsed: boolean; isOpen: boolean }>`
  padding: 16px 24px;
  color: inherit;
  display: flex;
  align-items: center;
  gap: ${({ collapsed }) => (collapsed ? '0' : '12px')};
  justify-content: ${({ collapsed }) => (collapsed ? 'center' : 'space-between')};
  cursor: pointer;

  &:hover {
    background: rgba(176, 48, 82, 0.18);
  }
`;

const Label = styled.span<{ collapsed: boolean }>`
  display: ${({ collapsed }) => (collapsed ? 'none' : 'inline')};
`;

const LogoToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  padding: 16px;
  font-size: 18px;
  cursor: pointer;
  &:hover {
    background: rgba(176, 48, 82, 0.18);
  }
`;

const LogoutButton = styled.button<{ collapsed: boolean }>`
  background: none;
  border: none;
  color: #fff;
  padding: 16px 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ collapsed }) => (collapsed ? '0' : '12px')};
  justify-content: ${({ collapsed }) => (collapsed ? 'center' : 'flex-start')};
  width: 100%;
  &:hover { background: rgba(176, 48, 82, 0.18); }
`;

const Content = styled.main`
  flex:1;
  background:#f5f6fa;
  padding:32px;
  overflow-y:auto;
  @media (max-width: 489px) {
    padding: 16px;
  }
`;

const TopBar = styled.header`
  height: 56px;
  background: linear-gradient(180deg, #2a0a0f 0%, #3D0301 100%);
  color: #fff;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 12px;
  position: relative;
`;

const TopBarTitle = styled.div`
  font-weight: 700;
  font-size: 16px;
`;

const TopBarCenter = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-weight: 700;
  font-size: 16px;
`;

const MobileNav = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const MobileNavMain = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const MobileItem = styled.div`
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  color: #222;
  &:hover { background: #f5f5f5; }
`;

const MobileLogout = styled.div`
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  color: #d32029;
  border-top: 1px solid #f0f0f0;
`;

const LimitedAdminBadge = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #fa8c16;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  vertical-align: middle;
`;

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
`;

const AdminLayout = () => {
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [slotsSubmenuOpen, setSlotsSubmenuOpen] = useState(false);
  const [isTiny, setIsTiny] = useState(false); // <= 489px
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // 브라우저 탭으로 돌아올 때 변경된 시간 제한 정책을 반영한다.
  useEffect(() => {
    const refreshPolicyOnVisible = () => {
      if (document.visibilityState === 'visible') {
        dispatch(refreshMeThunk());
      }
    };
    document.addEventListener('visibilitychange', refreshPolicyOnVisible);
    return () => {
      document.removeEventListener('visibilitychange', refreshPolicyOnVisible);
    };
  }, [dispatch]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login', { replace: true });
    // 로그인 화면으로 이동 후 한 번 새로고침하여 캐시된 화면/상태를 완전히 초기화
    setTimeout(() => window.location.reload(), 0);
  };

  const toggleSidebar = () => {
    setCollapsed((prev) => !prev);
  };

  const toggleSlotsSubmenu = () => {
    if (!collapsed) {
      setSlotsSubmenuOpen(prev => !prev);
    }
  };

  // 현재 경로에 따라 서브메뉴 자동 열기
  React.useEffect(() => {
    if (location.pathname.startsWith('/slots') && !collapsed) {
      setSlotsSubmenuOpen(true);
    }
  }, [location.pathname, collapsed]);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTiny(window.innerWidth <= 489);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isCollapsed = isMobile || collapsed;
  const showSidebar = user && !isTiny;
  const isSlackNotifyTarget = !!user && stripSuffix(user.username).toLowerCase() === 'adpa99';

  const pageTitle = React.useMemo(() => {
    const p = location.pathname;
    if (p.startsWith('/notices')) return '공지사항';
    if (p.startsWith('/accounts')) return '계정관리';
    if (p.startsWith('/slots-inactive')) return '비활성 슬롯';
    if (p.startsWith('/slots')) return '활성 슬롯';
    if (p.startsWith('/keyword-optimization')) return '키워드별 순위';
    if (p.startsWith('/url-rank')) return '링크 순위 확인';
    if (p.startsWith('/logs')) return '로그관리';
    if (p.startsWith('/slack-notify-settings')) return 'Slack 로그 알림 설정';
    if (p.startsWith('/profile') || p.startsWith('/mypage')) return '마이페이지';
    return '리워드 리프트';
  }, [location.pathname]);

  return (
    <Layout>
      {showSidebar && (
        <Sidebar collapsed={isCollapsed}>
          <LogoToggleWrapper>
            {!isCollapsed && <Logo collapsed={isCollapsed}>리워드 리프트</Logo>}
            <ToggleButton onClick={toggleSidebar}>
              {isCollapsed ? <MenuOutlined /> : <CloseOutlined />}
            </ToggleButton>
          </LogoToggleWrapper>
          <Menu>
            <Item to="/notices" collapsed={isCollapsed}>
              <BellOutlined />
              <Label collapsed={isCollapsed}>공지사항</Label>
            </Item>
            <Item to="/accounts" collapsed={isCollapsed}>
              <TeamOutlined />
              <Label collapsed={isCollapsed}>계정관리</Label>
            </Item>
            
            {/* 슬롯관리 - ADMIN은 하위 메뉴 2개, 그 외는 단일 링크 */}
            {user.grade === 'ADMIN' ? (
              <>
                <MenuHeader collapsed={isCollapsed} isOpen={slotsSubmenuOpen} onClick={() => {
                  if (isCollapsed) {
                    navigate('/slots');
                  } else {
                    toggleSlotsSubmenu();
                  }
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <AppstoreOutlined />
                    <Label collapsed={isCollapsed}>슬롯관리</Label>
                  </div>
                  {!isCollapsed && (slotsSubmenuOpen ? <DownOutlined style={{ fontSize: '12px' }} /> : <RightOutlined style={{ fontSize: '12px' }} />)}
                </MenuHeader>
                <SubMenuContainer collapsed={isCollapsed} isOpen={slotsSubmenuOpen}>
                  <SubItem to="/slots" collapsed={isCollapsed}><Label collapsed={isCollapsed}>활성 슬롯</Label></SubItem>
                  <SubItem to="/slots-inactive" collapsed={isCollapsed}><Label collapsed={isCollapsed}>비활성 슬롯</Label></SubItem>
                </SubMenuContainer>
              </>
            ) : (
              <Item to="/slots" collapsed={isCollapsed}>
                <AppstoreOutlined />
                <Label collapsed={isCollapsed}>슬롯 확인</Label>
              </Item>
            )}
            
            {/* 키워드별 순위 - ADMIN만 표시 */}
            {user.grade !== 'SELLER' && (
              <Item to="/keyword-optimization" collapsed={isCollapsed}>
                <SearchOutlined />
                <Label collapsed={isCollapsed}>키워드별 순위</Label>
              </Item>
            )}

            {/* 링크 순위 확인 - ADMIN만 표시 */}
            {user.grade === 'ADMIN' && (
              <Item to="/url-rank" collapsed={isCollapsed}>
                <LinkOutlined />
                <Label collapsed={isCollapsed}>링크 순위 확인</Label>
              </Item>
            )}
            
            <Item to="/logs" collapsed={isCollapsed}>
              <FileTextOutlined />
              <Label collapsed={isCollapsed}>로그관리</Label>
            </Item>
            {isSlackNotifyTarget && (
              <Item to="/slack-notify-settings" collapsed={isCollapsed}>
                <SettingOutlined />
                <Label collapsed={isCollapsed}>Slack 로그 알림</Label>
              </Item>
            )}
            <Item to="/profile" collapsed={isCollapsed}>
              <UserOutlined />
              <Label collapsed={isCollapsed}>
                마이페이지
                {isLimitedPartnerAdmin(user) && !isCollapsed && (
                  <LimitedAdminBadge>제한형 관리자</LimitedAdminBadge>
                )}
              </Label>
            </Item>

            <BottomArea>
              <LogoutButton onClick={handleLogout} collapsed={isCollapsed}>
                <LogoutOutlined />
                <Label collapsed={isCollapsed}>로그아웃</Label>
              </LogoutButton>
            </BottomArea>
          </Menu>
        </Sidebar>
      )}
      {user && isTiny && (
        <MobileContainer>
          <TopBar>
            <button onClick={() => setMobileMenuOpen(true)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <MenuOutlined />
            </button>
            <TopBarCenter>{pageTitle}</TopBarCenter>
          </TopBar>
          <Drawer
            placement="left"
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            width={260}
            bodyStyle={{ padding: 0 }}
          >
            <MobileNav>
              <MobileNavMain>
                <MobileItem onClick={() => { navigate('/notices'); setMobileMenuOpen(false); }}>
                  <BellOutlined /> 공지사항
                </MobileItem>
                <MobileItem onClick={() => { navigate('/accounts'); setMobileMenuOpen(false); }}>
                  <TeamOutlined /> 계정관리
                </MobileItem>

                {user.grade === 'ADMIN' ? (
                  <>
                    <MobileItem onClick={() => { navigate('/slots'); setMobileMenuOpen(false); }}>
                      <AppstoreOutlined /> 활성 슬롯
                    </MobileItem>
                    <MobileItem onClick={() => { navigate('/slots-inactive'); setMobileMenuOpen(false); }}>
                      <AppstoreOutlined /> 비활성 슬롯
                    </MobileItem>
                  </>
                ) : (
                  <MobileItem onClick={() => { navigate('/slots'); setMobileMenuOpen(false); }}>
                    <AppstoreOutlined /> 슬롯 확인
                  </MobileItem>
                )}

                {user.grade === 'ADMIN' && (
                  <MobileItem onClick={() => { navigate('/keyword-optimization'); setMobileMenuOpen(false); }}>
                    <SearchOutlined /> 키워드별 순위
                  </MobileItem>
                )}

                {user.grade === 'ADMIN' && (
                  <MobileItem onClick={() => { navigate('/url-rank'); setMobileMenuOpen(false); }}>
                    <LinkOutlined /> 링크 순위 확인
                  </MobileItem>
                )}

                <MobileItem onClick={() => { navigate('/logs'); setMobileMenuOpen(false); }}>
                  <FileTextOutlined /> 로그관리
                </MobileItem>
                {isSlackNotifyTarget && (
                  <MobileItem onClick={() => { navigate('/slack-notify-settings'); setMobileMenuOpen(false); }}>
                    <SettingOutlined /> Slack 로그 알림
                  </MobileItem>
                )}
                <MobileItem onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }}>
                  <UserOutlined /> 마이페이지
                </MobileItem>
              </MobileNavMain>
              <MobileLogout onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>
                <LogoutOutlined /> 로그아웃
              </MobileLogout>
            </MobileNav>
          </Drawer>
          <Content><Outlet /></Content>
        </MobileContainer>
      )}
      {!isTiny && <Content><Outlet /></Content>}
    </Layout>
  );
};

export default AdminLayout;
